import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from './Config';
import './PublishedVideosPage.css';

function PublishedVideosPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [publishedVideos, setPublishedVideos] = useState([]);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToLearning, setAddingToLearning] = useState(false);
  const [learningProgress, setLearningProgress] = useState('');

  // Function to get language display name
  const getLanguageDisplayName = (langCode) => {
    const languageNames = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ar': 'Arabic',
      'hi': 'Hindi',
      'ta': 'Tamil',
      'te': 'Telugu',
      'id': 'Indonesian'
    };
    return languageNames[langCode] || langCode.toUpperCase();
  };

  // Fetch project data
  const fetchProjectData = async () => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setProjectData(data);
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  };

  // Fetch published videos using the new dedicated API
  const fetchPublishedVideos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/published-videos/${projectId}`);

      if (response.ok) {
        const data = await response.json();
        setPublishedVideos(data.videos || []);
      } else {
        setError('Failed to fetch published videos');
      }
    } catch (error) {
      console.error('Error fetching published videos:', error);
      setError('Error fetching published videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
    fetchPublishedVideos();
  }, [projectId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getYouTubeVideoId = (url) => {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/);
    return match ? match[1] : null;
  };

  const getThumbnailUrl = (videoUrl) => {
    const videoId = getYouTubeVideoId(videoUrl);
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  };

  // Language code mapping - from video ISO codes to learning folder codes
  const languageMapping = {
    'en': 'original',
    'hi': 'hindi',
    'ar': 'arabic',
    'ta': 'tamil',
    'te': 'telugu',
    'de': 'german',
    'it': 'italian',
    'pt': 'portuguese',
    'ru': 'russian'
  };

  // Add to Learning functionality
  const handleAddToLearning = async () => {
    if (!projectData || publishedVideos.length === 0) {
      alert('No project data or published videos found.');
      return;
    }

    const confirmed = window.confirm(
      `Add all ${publishedVideos.length} published videos to learning folders?\n\n` +
      `This will add each video to all available language learning folders for the master project.`
    );

    if (!confirmed) return;

    setAddingToLearning(true);
    setLearningProgress('Fetching learning folders...');

    try {
      // Get learning folders for the master project
      const learningResponse = await fetch(`${API_URL}/learning-folders/master-project/${projectData.master_project_id}`);
      if (!learningResponse.ok) {
        throw new Error('Failed to fetch learning folders. Make sure learning folders exist for this master project.');
      }

      const learningFolders = await learningResponse.json();
      if (!learningFolders || learningFolders.length === 0) {
        throw new Error('No learning folders found for this master project. Please create learning folders first.');
      }

      // Get project sequence within master project
      const projectsResponse = await fetch(`${API_URL}/projects`);
      const allProjects = await projectsResponse.json();
      const masterProjects = allProjects.filter(p => p.master_project_id === projectData.master_project_id);
      const sortedProjects = masterProjects.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const projectSequence = sortedProjects.findIndex(p => p.id === parseInt(projectId)) + 1;

      let totalVideos = publishedVideos.length;
      let totalLanguages = learningFolders.length;
      let successCount = 0;
      let failCount = 0;
      let currentCount = 0;

      setLearningProgress(`Processing ${totalVideos} videos across ${totalLanguages} languages...`);

      // Process each video
      for (let videoIndex = 0; videoIndex < publishedVideos.length; videoIndex++) {
        const video = publishedVideos[videoIndex];

        // Find the matching language folder for this video
        const learningFolderCode = languageMapping[video.language_code];
        const matchingFolder = learningFolders.find(folder => folder.language_code === learningFolderCode);

        if (!matchingFolder) {
          console.warn(`No learning folder found for video language: ${video.language_code} (mapped to: ${learningFolderCode})`);
          failCount++;
          continue;
        }

        currentCount++;
        setLearningProgress(
          `Processing video ${videoIndex + 1}/${totalVideos} - ${video.language_name} (${currentCount}/${totalVideos})`
        );

        try {
          const payload = {
            es_training_topic_id: String(matchingFolder.external_id),
            title: video.title,
            description: video.description || '',
            language: matchingFolder.api_language_code,
            video_link: video.video_url,
            sequence: projectSequence,
            tag: "Beginner"
          };

          const response = await fetch(`${API_URL}/learning-folders/add-video`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              successCount++;
              console.log(`✅ Added "${video.title}" to ${video.language_code} learning folder`);
            } else {
              failCount++;
              console.error(`❌ Failed to add "${video.title}" to ${video.language_code}:`, result.message);
            }
          } else {
            failCount++;
            const errorData = await response.json();
            console.error(`❌ API error for "${video.title}" in ${video.language_code}:`, errorData);
          }
        } catch (error) {
          failCount++;
          console.error(`❌ Network error for "${video.title}" in ${video.language_code}:`, error);
        }

        // Small delay to avoid overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Show final results
      const totalAttempts = totalVideos;
      if (failCount === 0) {
        alert(`✅ Successfully added all videos to learning folders!\n\n` +
              `📹 Videos processed: ${totalVideos}\n` +
              `✅ Successfully added: ${successCount}/${totalAttempts}`);
      } else {
        alert(`⚠️ Add to learning completed with some issues:\n\n` +
              `📹 Videos processed: ${totalVideos}\n` +
              `✅ Successful: ${successCount}/${totalAttempts}\n` +
              `❌ Failed: ${failCount}/${totalAttempts}\n\n` +
              `Check console for detailed error messages.`);
      }

    } catch (error) {
      console.error('Error adding to learning:', error);
      alert(`Failed to add videos to learning: ${error.message}`);
    } finally {
      setAddingToLearning(false);
      setLearningProgress('');
    }
  };

  if (loading) {
    return (
      <div className="published-videos-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading published videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="published-videos-page">
      {/* Header */}
      <div className="page-header">
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          className="back-button"
        >
          ← Back to Project
        </button>

        <div className="header-content">
          <h1>Published Videos</h1>
          {projectData && (
            <p className="project-title">{projectData.title}</p>
          )}
        </div>

        {/* Add to Learning Button */}
        {publishedVideos.length > 0 && projectData?.master_project_id && (
          <div className="header-actions">
            <button
              onClick={handleAddToLearning}
              className="add-to-learning-button"
              disabled={addingToLearning}
            >
              {addingToLearning ? '⏳ Processing...' : '📚 Add to Learning'}
            </button>
          </div>
        )}
      </div>

      {/* Progress Indicator */}
      {addingToLearning && learningProgress && (
        <div className="progress-indicator">
          <div className="progress-content">
            <div className="progress-spinner"></div>
            <p>{learningProgress}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="page-content">
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {publishedVideos.length === 0 ? (
          <div className="no-videos">
            <div className="no-videos-icon">📺</div>
            <h2>No Published Videos</h2>
            <p>No videos have been published to YouTube for this project yet.</p>
            <button
              onClick={() => navigate(`/project/${projectId}/deploy`)}
              className="deploy-button"
            >
              Go to Deploy Page
            </button>
          </div>
        ) : (
          <div className="videos-grid">
            {publishedVideos.map((video, index) => (
              <div key={video.id || index} className="video-card">
                {/* Thumbnail */}
                <div className="video-thumbnail">
                  {getThumbnailUrl(video.video_url) ? (
                    <img
                      src={getThumbnailUrl(video.video_url)}
                      alt={`${video.language_name} thumbnail`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      📺
                    </div>
                  )}

                  <div className="language-badge">
                    {video.language_name}
                  </div>
                </div>

                {/* Video Info */}
                <div className="video-info">
                  <h3 className="video-title">{video.title}</h3>
                  <p className="video-description">
                    {video.description?.substring(0, 120)}
                    {video.description?.length > 120 && '...'}
                  </p>

                  <div className="video-meta">
                    <span className="publish-date">
                      📅 {formatDate(video.created_at)}
                    </span>
                    {video.video_id && (
                      <span className="video-id">
                        🆔 {video.video_id}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="video-actions">
                    <a
                      href={video.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="youtube-link"
                    >
                      🎥 View on YouTube
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Statistics */}
        {publishedVideos.length > 0 && (
          <div className="statistics">
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">{publishedVideos.length}</div>
                <div className="stat-label">Published Videos</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  {new Set(publishedVideos.map(v => v.language_code)).size}
                </div>
                <div className="stat-label">Languages</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  {publishedVideos.filter(v =>
                    new Date(v.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                  ).length}
                </div>
                <div className="stat-label">Published This Week</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublishedVideosPage;